import React from 'react';
import { Box, Container, Typography, Button, Grid2 as Grid, Card, CardContent, Chip, Stack } from '@mui/material';
import { CalendarMonth, Photo, Lock, FolderOpen } from '@mui/icons-material';
import { Authenticator } from "@aws-amplify/ui-react";
import '@aws-amplify/ui-react/styles.css';

const Home = () => {
	return (
		<Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
			{/* Hero Section with Auth */}
			<Box sx={{ bgcolor: 'primary.main', color: 'white', py: 8 }}>
				<Container maxWidth="lg">
					<Grid container spacing={4} alignItems="center">
						{/* Left: Hero Content */}
						<Grid size={{ xs: 12, md: 'grow' }}>
							<Typography variant="h2" component="h1" gutterBottom fontWeight="bold">
								Your Life, Organized by Date
							</Typography>
							<Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
								A personal diary and photo album where every memory has its place in time
							</Typography>
							<Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
								Do you remember your first kiss, first steps of your child, your biggest victory? 
								A lot of events happen in our life, but we don't always remember the date. 
								Create comfortable storage for your memories!
							</Typography>
							<Stack direction="row" spacing={2} sx={{ display: { xs: 'none', md: 'flex' } }}>
								<Button 
									variant="contained" 
									size="large" 
									sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
									href="/demo"
								>
									Explore Demo
								</Button>
							</Stack>
						</Grid>
						
						{/* Right: Auth Form */}
						<Grid size={{ xs: 12, md: 5 }} sx={{ minWidth: { xs: '100%', sm: 412, md: 520 } }}>
							<Box sx={{ 
								bgcolor: 'white', 
								borderRadius: { xs: 0, md: 2 }, 
								p: { xs: 0, md: 3 },
								boxShadow: { xs: 0, md: 3 }
							}}>
								<Authenticator loginMechanisms={['username']} />
							</Box>
						</Grid>
					</Grid>
				</Container>
			</Box>

			{/* Demo Section */}
			<Container maxWidth="lg" sx={{ py: 6 }}>
				<Card sx={{ bgcolor: 'info.light', border: '2px solid', borderColor: 'info.main' }}>
					<CardContent sx={{ p: 4, textAlign: 'center' }}>
						<Typography variant="h5" gutterBottom fontWeight="bold">
							👀 See It In Action
						</Typography>
						<Typography variant="body1" sx={{ mb: 2 }}>
							Explore our demo profile to see how events are organized by date
						</Typography>
						<Button 
							variant="contained" 
							size="large"
							startIcon={<FolderOpen />}
							href="/demo"
						>
							Visit everyday.events/demo
						</Button>
					</CardContent>
				</Card>
			</Container>

			{/* Features Section */}
			<Container maxWidth="lg" sx={{ py: 6 }}>
				<Typography variant="h3" textAlign="center" gutterBottom fontWeight="bold" sx={{ mb: 5 }}>
					What You Can Do
				</Typography>
				<Grid container spacing={4}>
					<Grid size={{ xs: 12, md: 4 }}>
						<Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
							<CardContent>
								<CalendarMonth sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
								<Typography variant="h5" gutterBottom fontWeight="bold">
									Date-Based Organization
								</Typography>
								<Typography variant="body1" color="text.secondary">
									Navigate through your memories by year, month, and day. Find any event instantly by its date.
								</Typography>
							</CardContent>
						</Card>
					</Grid>
					<Grid size={{ xs: 12, md: 4 }}>
						<Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
							<CardContent>
								<Photo sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
								<Typography variant="h5" gutterBottom fontWeight="bold">
									Photos & Videos
								</Typography>
								<Typography variant="body1" color="text.secondary">
									Upload photos and videos to your events. Create visual diaries or complete media albums.
								</Typography>
							</CardContent>
						</Card>
					</Grid>
					<Grid size={{ xs: 12, md: 4 }}>
						<Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
							<CardContent>
								<Lock sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
								<Typography variant="h5" gutterBottom fontWeight="bold">
									Private or Public
								</Typography>
								<Typography variant="body1" color="text.secondary">
									Keep events private for your eyes only, or make them public to share via direct link.
								</Typography>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</Container>

			{/* How It Works Section */}
			<Box sx={{ bgcolor: 'white', py: 6 }}>
				<Container maxWidth="lg">
					<Typography variant="h3" textAlign="center" gutterBottom fontWeight="bold" sx={{ mb: 5 }}>
						How It Works
					</Typography>
					<Grid container spacing={4} alignItems="center">
						<Grid item xs={12} md={6}>
							<Typography variant="h6" gutterBottom fontWeight="bold">
								1. Create Events
							</Typography>
							<Typography variant="body1" paragraph>
								Add a title and date (required). Optionally include description, photos, and videos.
							</Typography>
							<Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mt: 3 }}>
								2. Navigate by Date
							</Typography>
							<Typography variant="body1" paragraph>
								Your URL structure: everyday.events/username/year/month/day
							</Typography>
							<Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mt: 3 }}>
								3. View Statistics
							</Typography>
							<Typography variant="body1">
								See event, photo, and video counts for each year, month, and day at a glance.
							</Typography>
						</Grid>
						<Grid item xs={12} md={6}>
							<Card sx={{ bgcolor: 'grey.100', p: 3 }}>
								<Typography variant="body2" fontFamily="monospace" sx={{ mb: 1 }}>
									everyday.events/john
								</Typography>
								<Typography variant="caption" display="block" sx={{ mb: 2 }}>→ View all years</Typography>
								<Typography variant="body2" fontFamily="monospace" sx={{ mb: 1 }}>
									everyday.events/john/2025
								</Typography>
								<Typography variant="caption" display="block" sx={{ mb: 2 }}>→ View all months in 2025</Typography>
								<Typography variant="body2" fontFamily="monospace" sx={{ mb: 1 }}>
									everyday.events/john/2025/01
								</Typography>
								<Typography variant="caption" display="block" sx={{ mb: 2 }}>→ View all days in January</Typography>
								<Typography variant="body2" fontFamily="monospace" sx={{ mb: 1 }}>
									everyday.events/john/2025/01/15
								</Typography>
								<Typography variant="caption" display="block">→ View all events on Jan 15</Typography>
							</Card>
						</Grid>
					</Grid>
				</Container>
			</Box>

			{/* Pricing Section */}
			<Container maxWidth="lg" sx={{ py: 6 }}>
				<Typography variant="h3" textAlign="center" gutterBottom fontWeight="bold" sx={{ mb: 5 }}>
					Simple Pricing
				</Typography>
				<Grid container spacing={4} justifyContent="center">
					<Grid item xs={12} md={4}>
						<Card sx={{ height: '100%', border: '2px solid', borderColor: 'success.main' }}>
							<CardContent sx={{ textAlign: 'center', p: 4 }}>
								<Chip label="Free Forever" color="success" sx={{ mb: 2 }} />
								<Typography variant="h4" gutterBottom fontWeight="bold">
									500 MB
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
									Free storage for photos and videos
								</Typography>
								<Typography variant="body1">✓ Unlimited events</Typography>
								<Typography variant="body1">✓ Private & public events</Typography>
								<Typography variant="body1">✓ Date-based navigation</Typography>
							</CardContent>
						</Card>
					</Grid>
					<Grid item xs={12} md={4}>
						<Card sx={{ height: '100%' }}>
							<CardContent sx={{ textAlign: 'center', p: 4 }}>
								<Typography variant="h4" gutterBottom fontWeight="bold">
									Paid Tiers
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
									For larger storage needs
								</Typography>
								<Typography variant="body1">Pay only for storage used</Typography>
								<Typography variant="body1">Multiple subscription tiers</Typography>
								<Typography variant="body1">All features included</Typography>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</Container>

			{/* CTA Section */}
			<Box id="get-started" sx={{ bgcolor: 'primary.main', color: 'white', py: 8 }}>
				<Container maxWidth="md" sx={{ textAlign: 'center' }}>
					<Typography variant="h3" gutterBottom fontWeight="bold">
						Start Organizing Your Memories Today
					</Typography>
					<Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
						Free to start. Sign up above to begin.
					</Typography>
				</Container>
			</Box>
		</Box>
	);
}

export default Home;
